import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Zap, MessageSquare, AlertCircle, FileCheck, 
  Activity, TrendingUp, Search, RefreshCw, Bot,
  Loader2, CheckCircle2, XCircle, Timer, Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LegionCommandCenter = () => {
  const [stats, setStats] = useState({ active_bots: 4, total_conversations: 0, contracts_triggered: 0 });
  const [workflows, setWorkflows] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const [statusRes, workflowRes, feedRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/legion/status`),
        axios.get(`${BACKEND_URL}/api/legion/workflows`),
        axios.get(`${BACKEND_URL}/api/legion/activity-feed?limit=20`)
      ]);
      setStats(statusRes.data);
      setWorkflows(workflowRes.data || []);
      setActivity(feedRes.data || []);
    } catch (err) {
      console.error("Operational Hub Refresh Failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    refresh();
    // Throttle refresh to every 15 seconds to prevent browser lock-up
    const timer = setInterval(refresh, 15000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="max-w-7xl mx-auto p-10 space-y-12 page-transition">
      <header className="flex items-center justify-between">
        <div>
          <Badge variant="outline" className="mb-2 font-mono uppercase tracking-tighter opacity-50">Terminal Session Active</Badge>
          <h1 className="text-2xl font-black tracking-tighter uppercase italic text-slate-900">Operations Hub</h1>
          <p className="text-xs text-slate-500 font-mono">System Integrity: 99.9% // Real-Time Orchestration Online</p>
        </div>
        <Button onClick={refresh} variant="outline" className="h-12 w-12 rounded-xl border-2 hover:bg-slate-50 transition-all shadow-sm">
           <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
        </Button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard title="Active Units" value={stats.active_bots} icon={<Bot size={18}/>} />
        <StatCard title="Uptime" value="100%" icon={<Shield size={18}/>} />
        <StatCard title="Throughput" value="14.2k/hr" icon={<TrendingUp size={18}/>} />
        <StatCard title="Verified Data" value="98.2%" icon={<Zap size={18}/>} />
      </div>

      <div className="grid grid-cols-12 gap-8">
         {/* Live Workflows Panel */}
         <div className="col-span-12 lg:col-span-8 space-y-6">
            <Card className="border-2 border-slate-100 shadow-xl shadow-slate-200/40 rounded-3xl overflow-hidden bg-white">
               <CardHeader className="bg-slate-900 text-white p-8">
                  <div className="flex items-center gap-3">
                     <Activity size={20} className="text-emerald-400 animate-pulse" />
                     <CardTitle className="text-xl font-black uppercase italic tracking-widest">Active Workflows</CardTitle>
                  </div>
                  <CardDescription className="text-slate-400 font-medium text-xs uppercase tracking-tight">Real-time state tracking for autonomous acquisition units.</CardDescription>
               </CardHeader>
               <CardContent className="p-0">
                  <div className="divide-y divide-slate-50">
                     {workflows.map((w, i) => (
                        <div key={i} className="p-8 space-y-4 hover:bg-slate-50 transition-colors">
                           <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                 <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center font-black text-slate-900 uppercase">
                                    {w.bot_type?.charAt(0)}
                                 </div>
                                 <div>
                                    <p className="text-sm font-black uppercase tracking-tight">{w.bot_type} Workflow</p>
                                    <p className="text-[10px] font-mono text-slate-400 uppercase">{w.id.substring(0, 8)} • {w.status}</p>
                                 </div>
                              </div>
                              <Badge className={w.status === 'completed' ? 'bg-emerald-500' : 'bg-slate-900'}>
                                 {w.status.toUpperCase()}
                              </Badge>
                           </div>
                           <div className="space-y-2">
                              <div className="flex justify-between text-[10px] font-black uppercase text-slate-400">
                                 <span>{w.logs[w.logs.length - 1]?.message}</span>
                                 <span>{w.progress}%</span>
                              </div>
                              <Progress value={w.progress} className="h-1.5 bg-slate-100" />
                           </div>
                        </div>
                     ))}
                     {workflows.length === 0 && (
                        <div className="p-20 text-center space-y-4 opacity-20 italic font-medium">
                           <Timer size={40} className="mx-auto" />
                           <p>No active workflows identified.</p>
                        </div>
                     )}
                  </div>
               </CardContent>
            </Card>
         </div>

         {/* Activity Log (Side) */}
         <div className="col-span-12 lg:col-span-4 space-y-6">
            <Card className="border-2 border-slate-100 rounded-3xl shadow-sm overflow-hidden bg-white h-full">
               <CardHeader className="border-b border-slate-50 p-6">
                  <CardTitle className="text-xs font-black uppercase tracking-widest text-slate-400">Activity Stream</CardTitle>
               </CardHeader>
               <CardContent className="p-0">
                  <div className="divide-y divide-slate-50 max-h-[600px] overflow-y-auto font-sans">
                     {activity.map((act, i) => (
                        <div key={i} className="p-5 flex items-start gap-4 hover:bg-slate-50 transition-all group">
                           <div className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)] group-hover:scale-150 transition-transform" />
                           <div className="space-y-1">
                              <p className="text-[11px] font-bold leading-tight text-slate-800">{act.summary || act.activity_type?.replace('_', ' ') || "SYSTEM LOG"}</p>
                              <p className="text-[9px] font-mono text-slate-400 uppercase">{act.timestamp?.substring(11, 16)} • {act.bot_type}</p>
                           </div>
                        </div>
                     ))}
                  </div>
               </CardContent>
            </Card>
         </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon }) => (
  <Card className="border-2 border-slate-100 bg-white hover:border-slate-900 transition-all rounded-3xl shadow-sm group">
    <CardContent className="p-8">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">{title}</p>
          <p className="text-2xl font-black tracking-tighter italic text-slate-900">{value}</p>
        </div>
        <div className="p-3 rounded-2xl bg-slate-50 text-slate-400 group-hover:text-slate-900 transition-colors">{icon}</div>
      </div>
    </CardContent>
  </Card>
);

export default LegionCommandCenter;
